import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import CalendarGrid from '../components/calendar/CalendarGrid';
import ReservationModal from '../components/modals/ReservationModal';
import ScopeSelectionDialog from '../components/modals/ScopeSelectionDialog';
import { useCalendar } from '../hooks/useCalendar';
import { useReservations } from '../hooks/useReservations';
import { useAuth } from '../context/AuthContext';
import { formatDate, addMinutesToTime, roundToQuarterHour, formatLocalDateTime } from '../utils/timeUtils';
import { CALENDAR_SETTINGS } from '../utils/constants';
import EmptyState from '../components/common/EmptyState';

/**
 * カレンダービューページ
 */
const CalendarPage = () => {
  const gridRef = useRef(null);
  const savedScrollPosition = useRef(null); // スクロール位置を保存
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { currentDate, viewMode, goToToday, goToPrevious, goToNext, getTitle, getWeekDates, getDateRange, setView } = useCalendar();

  // useMemoでメモ化して、無限ループを防ぐ
  const dateRange = useMemo(() => getDateRange(), [getDateRange]);
  const weekDates = useMemo(() => getWeekDates(), [getWeekDates]);

  const {
    reservations,
    loading,
    error,
    createReservation,
    updateReservation,
    deleteReservation,
    checkConflict,
  } = useReservations(dateRange.start, dateRange.end, {
    enabled: !authLoading && isAuthenticated, // 認証状態の確認が完了してから予約を取得
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [newReservationData, setNewReservationData] = useState(null);
  const [pageError, setPageError] = useState(null);

  // 繰り返し予約の scope 選択ダイアログ
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const [scopeActionType, setScopeActionType] = useState('edit'); // 'edit' or 'delete'
  const [pendingReservation, setPendingReservation] = useState(null);
  const [pendingData, setPendingData] = useState(null);

  // 新規作成された予約のハイライト
  const [newlyCreatedIds, setNewlyCreatedIds] = useState([]);

  // 週切り替え時のスライドアニメーション方向
  const [slideDirection, setSlideDirection] = useState(null); // 'left' | 'right' | null

  // 初回ロードかどうかを追跡（初回のみ全画面ローディング表示）
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  /**
   * 現在時刻に基づいてカレンダーを自動スクロール
   */
  const scrollToCurrentTime = () => {
    if (!gridRef.current) return;

    const { START_HOUR, END_HOUR, GRID_ROW_HEIGHT } = CALENDAR_SETTINGS;
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();

    let targetHour = currentHour;
    let targetMinutes = currentMinutes;

    // 営業時間外の処理（7:00-22:00）
    if (currentHour < START_HOUR) {
      // 7:00前 → 7:00を表示
      targetHour = START_HOUR;
      targetMinutes = 0;
    } else if (currentHour >= END_HOUR) {
      // 22:00以降 → 翌日7:00を表示
      targetHour = START_HOUR;
      targetMinutes = 0;
    }

    // スクロール位置の計算
    // (目標時刻 - 開始時刻) × 1時間あたりのピクセル高さ
    const scrollPosition =
      ((targetHour - START_HOUR) * 60 + targetMinutes) / 60 * GRID_ROW_HEIGHT;

    // 画面上部寄りに表示（スクロール位置から150px上にオフセット）
    const offsetTop = 150;
    const adjustedScroll = Math.max(0, scrollPosition - offsetTop);

    gridRef.current.scrollTop = adjustedScroll;
  };

  // スクロール位置を保存するハンドラ
  const handleScroll = () => {
    if (gridRef.current) {
      savedScrollPosition.current = gridRef.current.scrollTop;
    }
  };

  // カレンダーグリッドの高さを実測してCSS変数に反映
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;

    let isInitialLoad = true;

    const updateHeight = () => {
      if (!gridRef.current) return;
      const rect = gridRef.current.getBoundingClientRect();
      const bottomPadding = 0; // 必要に応じて下パディングを加味
      const heightPx = Math.max(0, window.innerHeight - rect.top - bottomPadding);
      gridRef.current.style.setProperty('--grid-height', `${heightPx}px`);

      // 初回ロード時のみ自動スクロール
      if (isInitialLoad) {
        // 次のフレームでスクロール実行（高さ設定が反映された後）
        requestAnimationFrame(() => {
          scrollToCurrentTime();
          // 初回スクロール後、位置を保存
          savedScrollPosition.current = gridRef.current?.scrollTop || 0;
        });
        isInitialLoad = false;
      }
    };

    // 初期計測（描画後）
    const raf = requestAnimationFrame(updateHeight);

    // ウィンドウリサイズ対応（スクロールはしない）
    window.addEventListener('resize', updateHeight);

    // スクロール位置を監視して保存
    el.addEventListener('scroll', handleScroll);

    // レイアウト変化に追従（フォントロードや上部要素の変化）
    const ro = new ResizeObserver(() => {
      // ResizeObserverの再入呼び出しを避けるため次フレームで実行
      requestAnimationFrame(updateHeight);
    });
    // 対象要素に限定して監視
    ro.observe(gridRef.current);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateHeight);
      el.removeEventListener('scroll', handleScroll);
      ro.disconnect();
    };
  }, []);

  // 週切り替え時にスクロール位置を復元
  useEffect(() => {
    if (!gridRef.current) return;

    // 保存されたスクロール位置がある場合は復元
    if (savedScrollPosition.current !== null && savedScrollPosition.current !== undefined) {
      // 次のフレームで復元（レンダリング後）
      requestAnimationFrame(() => {
        if (gridRef.current) {
          gridRef.current.scrollTop = savedScrollPosition.current;
        }
      });
    }
  }, [dateRange]); // dateRangeが変更されたら（週切り替え時）実行

  // 週切り替え時のスライドアニメーション効果をリセット
  useEffect(() => {
    if (slideDirection) {
      const timer = setTimeout(() => {
        setSlideDirection(null);
      }, 300); // アニメーション時間と同じ
      return () => clearTimeout(timer);
    }
  }, [slideDirection]);

  // 初回ロード完了を検知
  useEffect(() => {
    if (!loading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [loading, isInitialLoad]);

  /**
   * 前週へ移動（スライドアニメーション付き）
   */
  const handleGoToPrevious = () => {
    setSlideDirection('left');
    goToPrevious();
  };

  /**
   * 次週へ移動（スライドアニメーション付き）
   */
  const handleGoToNext = () => {
    setSlideDirection('right');
    goToNext();
  };

  // 認証状態の読み込み中はローディング表示
  if (authLoading) {
    return (
      <div className="loading-overlay">
        <div className="loading-spinner">認証確認中...</div>
      </div>
    );
  }

  // ログインしていない場合はログインページにリダイレクト
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  /**
   * 予約クリック時の処理
   */
  const handleReservationClick = (reservation) => {
    setSelectedReservation(reservation);
    setNewReservationData(null);
    setModalOpen(true);
  };

  /**
   * 空きスロットクリック時の処理（新規予約作成）
   */
  const handleEmptySlotClick = ({ roomType, date, time }) => {
    const startTime = roundToQuarterHour(time);
    const endTime = addMinutesToTime(startTime, 60); // デフォルト1時間

    setSelectedReservation(null);
    setNewReservationData({
      room_type: roomType,
      start_datetime: startTime,
      end_datetime: endTime,
    });
    setModalOpen(true);
  };

  /**
   * 予約ドロップ時の処理（ドラッグ&ドロップでの移動）
   */
  const handleReservationDrop = async ({ reservation, newRoomType, newTime }) => {
    if (!isAuthenticated) {
      setPageError('ログインが必要です');
      setTimeout(() => setPageError(null), 5000);
      return;
    }

    const startTime = roundToQuarterHour(newTime);
    const originalStart = new Date(reservation.start);
    const originalEnd = new Date(reservation.end);
    const duration = originalEnd - originalStart;
    const endTime = new Date(startTime.getTime() + duration);

    // G+B予約の場合は、元のroom_typeを維持（G列にドロップしても'GB'のまま）
    let targetRoomType = newRoomType;
    if (reservation.room_type === 'GB') {
      targetRoomType = 'GB';
    }

    // 通常予約からG+Bへの変更を防ぐ
    if ((reservation.room_type === 'G' || reservation.room_type === 'B') && targetRoomType === 'GB') {
      setPageError('通常予約をG+B予約に変更することはできません');
      setTimeout(() => setPageError(null), 5000);
      return;
    }

    // 営業時間チェック (7:00-22:00)
    const { START_HOUR, END_HOUR } = CALENDAR_SETTINGS;
    const startHour = startTime.getHours();
    const endHour = endTime.getHours();
    const endMinute = endTime.getMinutes();

    if (startHour < START_HOUR || startHour >= END_HOUR) {
      setPageError(`開始時刻は${START_HOUR}:00〜${END_HOUR}:00の間である必要があります`);
      setTimeout(() => setPageError(null), 5000);
      return;
    }

    if (endHour > END_HOUR || (endHour === END_HOUR && endMinute > 0)) {
      setPageError(`終了時刻は${END_HOUR}:00以前である必要があります`);
      setTimeout(() => setPageError(null), 5000);
      return;
    }

    // 競合チェック
    const conflictData = {
      room_type: targetRoomType,
      start_datetime: formatLocalDateTime(startTime),
      end_datetime: formatLocalDateTime(endTime),
      exclude_reservation_id: reservation.id,
    };

    const conflictResult = await checkConflict(conflictData);

    if (conflictResult.has_conflict) {
      const conflictTimes = conflictResult.conflicting_reservations
        .map(r => `${new Date(r.start).toLocaleTimeString('ja-JP', {hour: '2-digit', minute: '2-digit'})} - ${new Date(r.end).toLocaleTimeString('ja-JP', {hour: '2-digit', minute: '2-digit'})}`)
        .join(', ');
      setPageError(`指定した時間帯に競合する予約があります: ${conflictTimes}`);
      setTimeout(() => setPageError(null), 5000);
      return;
    }

    const updateData = {
      room_type: targetRoomType,
      start_datetime: formatLocalDateTime(startTime),
      end_datetime: formatLocalDateTime(endTime),
    };

    const result = await updateReservation(reservation.id, updateData);

    if (!result.success) {
      setPageError(result.error || '予約の移動に失敗しました');
      setTimeout(() => setPageError(null), 5000);
    }
  };

  /**
   * 予約の作成/更新
   */
  const handleSaveReservation = async (reservationData) => {
    let result;

    if (selectedReservation) {
      // 繰り返し予約の更新の場合、scopeダイアログを表示
      if (selectedReservation.is_recurring || selectedReservation.parent_reservation_id) {
        // scopeが未設定の場合、ダイアログを表示
        if (!reservationData.update_scope) {
          setPendingReservation(selectedReservation);
          setPendingData(reservationData);
          setScopeActionType('edit');
          setScopeDialogOpen(true);
          return { success: false, pending: true }; // ダイアログ待ち
        }
      }

      // 既存予約の更新
      result = await updateReservation(selectedReservation.id, reservationData);
    } else {
      // 新規予約の作成
      result = await createReservation(reservationData);

      // 新規作成成功時、ハイライトアニメーションを追加
      if (result.success && result.data) {
        const createdIds = Array.isArray(result.data)
          ? result.data.map(r => r.id)
          : [result.data.id];

        setNewlyCreatedIds(prev => [...prev, ...createdIds]);

        // 0.8秒後にハイライトを削除
        setTimeout(() => {
          setNewlyCreatedIds(prev => prev.filter(id => !createdIds.includes(id)));
        }, 800);
      }
    }

    if (result.success) {
      setModalOpen(false);
      setSelectedReservation(null);
      setNewReservationData(null);
    }

    // 結果を返してモーダルでエラーを表示できるようにする
    return result;
  };

  /**
   * 予約の削除
   */
  const handleDeleteReservation = async (reservationId, scope = null) => {
    // 繰り返し予約の削除の場合、scopeダイアログを表示
    if (selectedReservation && (selectedReservation.is_recurring || selectedReservation.parent_reservation_id) && !scope) {
      setPendingReservation(selectedReservation);
      setPendingData(null);
      setScopeActionType('delete');
      setScopeDialogOpen(true);
      return;
    }

    if (!window.confirm('この予約を削除してもよろしいですか？')) {
      return;
    }

    const result = await deleteReservation(reservationId, scope);

    if (result.success) {
      setModalOpen(false);
      setSelectedReservation(null);
    } else {
      alert(result.error || '予約の削除に失敗しました');
    }
  };

  /**
   * モーダルを閉じる
   */
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedReservation(null);
    setNewReservationData(null);
  };

  /**
   * スコープ選択の処理
   */
  const handleScopeSelect = async (scope) => {
    if (scopeActionType === 'edit' && pendingData) {
      // 編集の場合
      const dataWithScope = { ...pendingData, update_scope: scope };
      const result = await updateReservation(pendingReservation.id, dataWithScope);

      if (result.success) {
        setModalOpen(false);
        setSelectedReservation(null);
        setNewReservationData(null);
      } else {
        setPageError(result.error || '予約の更新に失敗しました');
        setTimeout(() => setPageError(null), 5000);
      }
    } else if (scopeActionType === 'delete') {
      // 削除の場合
      await handleDeleteReservation(pendingReservation.id, scope);
    }

    // pending状態をクリア
    setPendingReservation(null);
    setPendingData(null);
  };

  if (!isAuthenticated) {
    return (
      <div className="page">
        <div className="page-header">
          <h2>カレンダー</h2>
          <p>カレンダーを表示するにはログインが必要です。</p>
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-page">
      {/* カレンダーヘッダー */}
      <div className="calendar-header">
        <div className="calendar-navigation">
          <button onClick={handleGoToPrevious} className="nav-button" aria-label="前の週へ移動">
            ◀ 前週
          </button>
          <button onClick={goToToday} className="nav-button-today" aria-label="今週に戻る">
            今週
          </button>
          <button onClick={handleGoToNext} className="nav-button" aria-label="次の週へ移動">
            翌週 ▶
          </button>
          <div className="view-mode-toggle" role="group" aria-label="表示モード選択">
            <button
              onClick={() => setView('workweek')}
              className={viewMode === 'workweek' ? 'view-button active' : 'view-button'}
              aria-label="稼働日表示（月曜日から金曜日）"
              aria-pressed={viewMode === 'workweek'}
            >
              稼働日
            </button>
            <button
              onClick={() => setView('fullweek')}
              className={viewMode === 'fullweek' ? 'view-button active' : 'view-button'}
              aria-label="週表示（月曜日から日曜日）"
              aria-pressed={viewMode === 'fullweek'}
            >
              週
            </button>
          </div>
        </div>

        <div className="calendar-title">
          <h2 aria-live="polite" aria-atomic="true">{getTitle()}</h2>
        </div>

        <div className="calendar-actions">
          <button
            onClick={() => {
              setSelectedReservation(null);
              setNewReservationData({
                room_type: 'G',
                start_datetime: new Date(),
                end_datetime: addMinutesToTime(new Date(), 60),
              });
              setModalOpen(true);
            }}
            className="btn-primary"
          >
            + 新規予約
          </button>
        </div>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* ページエラー表示（alert代替） */}
      {pageError && (
        <div className="page-error-banner">
          <span>{pageError}</span>
          <button onClick={() => setPageError(null)} className="error-close-btn">
            ✕
          </button>
        </div>
      )}

      {/* ローディング表示：初回のみ全画面 */}
      {loading && isInitialLoad && (
        <div className="loading-overlay">
          <div className="loading-spinner">読み込み中...</div>
        </div>
      )}

      {/* 週切り替え時の小さなローディングインジケーター */}
      {loading && !isInitialLoad && (
        <div style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          zIndex: 1000,
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '8px 16px',
          borderRadius: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '13px',
          color: '#666'
        }}>
          <div className="spinner spinner-small spinner-primary"></div>
          更新中...
        </div>
      )}

      {/* 空状態 */}
      {!loading && (!reservations || reservations.length === 0) && (
        <EmptyState title="今週の予約はありません" description="空き枠をクリックして新規予約を作成できます。" />
      )}

      {/* カレンダーグリッド */}
      <CalendarGrid
        weekDates={weekDates}
        reservations={reservations}
        onReservationClick={handleReservationClick}
        onEmptySlotClick={handleEmptySlotClick}
        onReservationDrop={handleReservationDrop}
        viewMode={viewMode}
        gridRef={gridRef}
        newlyCreatedIds={newlyCreatedIds}
        slideDirection={slideDirection}
      />

      {/* 予約モーダル */}
      {modalOpen && (
        <ReservationModal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          reservation={selectedReservation}
          initialData={newReservationData}
          onSave={handleSaveReservation}
          onDelete={handleDeleteReservation}
        />
      )}

      {/* スコープ選択ダイアログ */}
      <ScopeSelectionDialog
        isOpen={scopeDialogOpen}
        onClose={() => {
          setScopeDialogOpen(false);
          setPendingReservation(null);
          setPendingData(null);
        }}
        onSelect={handleScopeSelect}
        actionType={scopeActionType}
      />
    </div>
  );
};

export default CalendarPage;
